import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Col, Row } from 'react-bootstrap';

import _ from 'lodash';
import { useDispatch, useSelector } from 'react-redux';
import { refreshDashboardData } from 'stores/dashboard';
import ActiveDevices from './ActiveDevices';
import DashboardKpiCard from './DashboardKpiCard';
import DashboardSubscriptionKpiCard from './DashboardSubscriptionKpiCard';
import NotaryMonthlyFee from './NotaryFeeMonthly';
import SigningWidget from './SigningWidget';
import TotalSales from './TotalSales';
import SessionByUses from './session-by-uses/SessionByUses';

import {
  faCircle,
  faDollarSign,
  faFileAlt,
  faFileContract,
  faFolderOpen,
  faMapMarkerAlt,
  faPencilAlt,
  faUsers
} from '@fortawesome/free-solid-svg-icons';
import { stringToColour } from 'helpers/utils';
import Greetings from './greetings/Greetings';
import IconAlert from 'components/common/IconAlert';
import draftEntryHelper from 'helpers/draft-entry-helper';
import moment from 'moment';
import { setDeleteAccountInfo } from 'stores/app';
import api from 'util/api';
import { setFeatures } from 'stores/authentication';
import userHelper from 'helpers/userHelper';
import { getLatestDashboardDateLabel } from './dashboardDates';
import { getDashboardPresetTimeframe } from './dashboardTimeframe';

const Dashboard = () => {
  const dispatch = useDispatch();
  const [loading, setLoading] = useState(false);
  const [dataFeeByCategory, setDataFeeByCategory] = useState(null);
  const [dataDocumentByType, setDataDocumentByType] = useState(null);
  const [daysRange, setDaysRange] = useState(0);
  const [timeframeLabel, setTimeframeLabel] = useState(
    getDashboardPresetTimeframe(7).label
  );

  const { userMaster, dataExportMonths, deleteUserInfo } = useSelector(
    state => state.app
  );
  const { dashboard } = useSelector(state => state);
  const { draft: dashboardDraft } = useSelector(state => state.dashboard);
  const initialized = useRef(null);

  useEffect(() => {
    if (userMaster && !initialized.current) {
      initDashboardData();
      initialized.current = true;
    }
  }, [userMaster, timeframeLabel]);

  const accountDeletedDate = useMemo(() => {
    const timezones = userHelper.getTimezoneObj(userMaster?.LocalTimezone);
    if (timezones?.zone && deleteUserInfo?.IsAccountDeleteRequested) {
      const parsedDate = moment.tz(deleteUserInfo?.AccountDeleteDate, 'UTC');
      return parsedDate.tz(timezones?.zone).format('MMM DD, YYYY');
    } else {
      return moment(timezones?.zone).format('MMM DD, YYYY');
    }
  }, [deleteUserInfo, userMaster]);
  const initDashboardData = () => {
    setLoading(true);
    const defaultTimeframe = getDashboardPresetTimeframe(7);
    dispatch(refreshDashboardData(defaultTimeframe.start, defaultTimeframe.end))
      .then(() => {
        setLoading(false);
      })
      .catch(err => {
        setLoading(false);
      });
  };

  useEffect(() => {
    setDataFeeByCategory(
      _.orderBy(
        renderUsesData(dashboard?.rawData?.Summary?.FeesByCategory),
        ['value'],
        ['desc']
      )
    );
    setDataDocumentByType(
      _.orderBy(
        renderUsesData(dashboard?.rawData?.Summary?.DocumentsByType),
        ['value'],
        ['desc']
      )
    );
  }, [dashboard]);

  const renderUsesData = dataset => {
    let tempData = [];
    let counter = 0;
    let totalValue = 0;
    _.map(dataset, function (value, key) {
      totalValue += parseFloat(value);
      counter++;
    });
    let avg = counter > 0 ? totalValue / counter : 0;

    _.map(dataset, function (value, key) {
      let progressValue = Math.round(parseInt(value) - avg, 1);
      let progressPercent = avg
        ? Math.round((progressValue / avg) * 100, 0)
        : 0;
      tempData.push({
        icon: faCircle,
        label: key,
        value: value,
        progress: progressValue >= 0,
        progressValue: progressValue,
        caret:
          parseFloat(progressValue) > 0
            ? 'fas fa-caret-up text-success'
            : 'fas fa-caret-down text-danger',
        color: stringToColour(key),
        progressPercent: progressPercent + '%'
      });
      //if (++counter > 2) return;
    });
    return tempData;
  };

  useEffect(() => {
    api.Token.detail('detail').then(res => {
      // Set feature flags into redux
      if (res?.User?.FeatureFlags) {
        dispatch(setFeatures(res.User.FeatureFlags || []));
      }
      if (res?.User?.IsAccountDeleteRequested) {
        dispatch(
          setDeleteAccountInfo({
            AccountDeleteDate: res.User.AccountDeleteDate,
            AccountDeleteRequestedOn: res.User.AccountDeleteRequestedOn,
            AccountDeleteType: res.User.AccountDeleteType,
            IsAccountDeleteRequested: res.User.IsAccountDeleteRequested
          })
        );
      } else {
        dispatch(setDeleteAccountInfo({}));
      }
    });
  }, []);

  const formatNumber = value => {
    return Number(value || 0).toLocaleString(undefined, {
      maximumFractionDigits: 2
    });
  };

  const getLatestDraftLabel = () => {
    const latestDraft = _.orderBy(
      dashboardDraft?.Objects,
      'Updated',
      'desc'
    )[0];

    if (!latestDraft?.Updated) {
      return 'No drafts pending';
    }

    return `Updated ${moment(latestDraft.Updated).fromNow()}`;
  };

  const summary = dashboard?.rawData?.Summary || {};

  return (
    <>
      <Row className="g-3 mb-3">
        <Col sm="12">
          <Greetings
            setLoading={setLoading}
            loading={loading}
            setDaysRange={setDaysRange}
            setTimeframeLabel={setTimeframeLabel}
          />
        </Col>
        <Col sm="12 m-0">
          {deleteUserInfo?.IsAccountDeleteRequested && (
            <Alert variant="danger fs--1 m-0 mb-2">
              Your account deletion request has been received. Your account is
              scheduled to be permanently deleted on {accountDeletedDate} and
              all associated data will be removed.
            </Alert>
          )}
        </Col>
        {daysRange && daysRange != 7 && (
          <Col sm="12">
            <IconAlert variant={'info'}>
              <p className="mb-0">
                Large date ranges may take longer to load - please be patient
                while the dashboard populates with your data.
              </p>
            </IconAlert>
          </Col>
        )}
      </Row>
      <Row className="g-3 mb-3">
        <Col sm={6} lg={3}>
          <DashboardKpiCard
            title="Saved Drafts"
            value={dashboardDraft?.TotalRecords || 0}
            detail={timeframeLabel}
            footer={getLatestDraftLabel()}
            icon={faPencilAlt}
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <DashboardKpiCard
            title="Signing Packages"
            value={dashboard.signingPackage?.length || 0}
            detail={timeframeLabel}
            footer={getLatestDashboardDateLabel(dashboard.signingPackage)}
            icon={faFolderOpen}
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <DashboardKpiCard
            title="Document Types"
            value={dashboard.documentType?.length || 0}
            detail={timeframeLabel}
            footer={getLatestDashboardDateLabel(dashboard.documentType)}
            icon={faFileContract}
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <DashboardKpiCard
            title="Saved Locations"
            value={dashboard.location?.length || 0}
            detail={timeframeLabel}
            footer={getLatestDashboardDateLabel(dashboard.location)}
            icon={faMapMarkerAlt}
            loading={loading}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col sm={6} lg={3}>
          <DashboardSubscriptionKpiCard />
        </Col>
        <Col sm={6} lg={3}>
          <DashboardKpiCard
            title="Fee Collected"
            value={`$${formatNumber(summary.FeesCollected)}`}
            detail={timeframeLabel}
            icon={faDollarSign}
            accent="success"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <DashboardKpiCard
            title="Documents in Journal"
            value={formatNumber(summary.DocumentCount)}
            detail={timeframeLabel}
            icon={faFileAlt}
            accent="info"
            loading={loading}
          />
        </Col>
        <Col sm={6} lg={3}>
          <DashboardKpiCard
            title="Signers in Journal"
            value={formatNumber(summary.SignerDistinctCount)}
            detail={timeframeLabel}
            icon={faUsers}
            accent="secondary"
            loading={loading}
          />
        </Col>
      </Row>

      <Row className="g-3 mb-3">
        <Col lg={6}>
          <SessionByUses
            loading={loading}
            data={dataFeeByCategory}
            title="Fees by Category"
            labelPrefix="$"
            xAxisLabel="Category"
            yAxisLabel="Fees ($)"
          />
        </Col>
        <Col lg={6}>
          <SessionByUses
            loading={loading}
            data={dataDocumentByType}
            title="Documents by Type"
            labelPrefix=""
            xAxisLabel="Document Type"
            yAxisLabel="Count"
          />
        </Col>
        <Col lg={6}>
          <SigningWidget loading={loading} />
        </Col>
        <Col lg={6}>
          <SigningWidget
            loading={loading}
            monthOffset={0}
            title="This Month's Journal"
            summaryPeriodLabel="current month"
            tooltipText="Notary eJournal completed in the current month"
          />
        </Col>
        <Col lg={6}>
          {dataExportMonths && (
            <TotalSales
              className="h-100"
              months={dataExportMonths}
              loading={loading}
            />
          )}
        </Col>
        <Col lg={6}>
          {dataExportMonths && (
            <NotaryMonthlyFee
              className="h-100"
              months={dataExportMonths}
              loading={loading}
            />
          )}
        </Col>
        <Col lg={6}>
          <ActiveDevices
            loading={loading}
            className="h-100"
            style={{ minHeight: '32rem' }}
            users={dashboard.device}
          />
        </Col>
      </Row>
    </>
  );
};

export default Dashboard;
